import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  Form,
  Label,
  Row,
  Nav,
  NavItem,
  CardTitle,
  Spinner,
} from "reactstrap";
import { toast } from "react-toastify";

import Button from "../../../components/Button/Button";
import Select from "../../../components/Form/Select/Select";
import Input from "../../../components/Form/Input";
import DatePicker from "../../../components/Form/DatePicker/DatePicker";
import labelPlugin from "flatpickr/dist/plugins/labelPlugin/labelPlugin";
import moment from "moment";

import Dropzone from "../../../components/Common/Dropzone";

import {
  getCustomeCode,
  getSelectOptions,
  getUserProfile,
} from "../../../helpers/functions_helper";
import { useFormik } from "formik";

import { validationSchemaStep2 } from "../utils/validation";
import { initialValues } from "../config/initialValues";
import immigrationService from "../services/service";

import "./styles.css";
import ProfileInfo from "./ProfileInfo";
import NavItems from "./NavItems";
import { ImmigrationDataType } from "../types";
import { mapImmigrationToForm } from "../mappers/mapper";

interface LocationState {
  immigrationData?: ImmigrationDataType;
}

const FormComponent = (props: any) => {
  const { id, immigration_id, application_id, isDocumentsTab } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  let navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  const searchParams = new URLSearchParams(location.search);
  const from = searchParams.get("from");
  const moduleAccess = getUserProfile("immigration");

  const toastId = React.useRef(null) as any;

  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState<any>(initialValues);
  const [activeTab, setActiveTab] = useState("immigration");
  const [activeApplicationId, setActiveApplicationId] = useState<number | null>(null);
  const [activeSection, setActiveSection] = useState("Documents");

  const handleRedirect = (isCancel = false) => {
    if (isCancel === true) {
      if (from) {
        return navigate(`${from}?redirect=true`);
      } else {
        return navigate(`/immigration/list?redirect=true`);
        // return navigate(`/immigration/update-immigration/${id}/${immigration_id}?redirect=true`);
      }
    }
  };

  const loadProfileData = async () => {
    setLoading(true);

    const res = await immigrationService.get({ id, immigration_id });

    if (res.data.status === "success") {
      const mapped = mapImmigrationToForm(res.data.data);

      setValues((prev: any) => ({
        ...prev,
        ...mapped,
      }));
    }

    setLoading(false);
  };

  const loadApplicationData = async () => {
    setLoading(true);

    const res = await immigrationService.get({ id, immigration_id, application_id });

    if (res.data.status === "success") {
      const mapped = mapImmigrationToForm(res.data.data);

      const selectedApplication = res.data.data?.applications?.find(
        (a: any) => String(a.id) === String(application_id),
      );

      setValues((prev: any) => ({
        ...mapped, // keep your mapper output

        // 🔥 override only application-specific fields
        ...(selectedApplication
          ? {
              application_id: selectedApplication.id,
              application_code: selectedApplication.application_id,

              application_visa_type_id: selectedApplication.application_visa_type_id,
              application_visa_type_value:
                selectedApplication.application_visa_type?.visa_type_value ?? "",

              application_country_id: selectedApplication.application_country_id,
              application_country_value: selectedApplication.application_country?.name ?? "",

              application_submission_date: selectedApplication.application_submission_date,

              application_status_id: selectedApplication.application_status_id,
              application_status_value:
                selectedApplication.application_status?.application_status_value ?? "",

              application_start_date: selectedApplication.application_start_date,
              application_expiry_date: selectedApplication.application_expiry_date,
              application_duration: selectedApplication.application_duration,

              number_of_entries_id: selectedApplication.number_of_entries_id,
              number_of_entries_value:
                selectedApplication.number_of_entries?.number_of_entries_value ?? "",

              issuing_authority: selectedApplication.issuing_authority,
              reference_number: selectedApplication.reference_number,
              application_center: selectedApplication.application_center,
              case_manager: selectedApplication.case_manager,
              assigned_team: selectedApplication.assigned_team,
              client_contact: selectedApplication.client_contact,
            }
          : {}),
      }));
    }

    setLoading(false);
  };

  useEffect(() => {
    if (state?.immigrationData) {
      const mapped = mapImmigrationToForm(state.immigrationData);

      setValues((prev: any) => ({
        ...prev,
        ...mapped,
      }));
      setLoading(false);
    } else if (id && immigration_id && !application_id) {
      loadProfileData();
    } else if (id && immigration_id && application_id) {
      loadApplicationData();
    }

    application_id && setActiveApplicationId(parseInt(application_id));
  }, [state, id, immigration_id, application_id]);

  return (
    <React.Fragment>
      <Row className="profile-layout">
        {/* Left Sidebar - Profile Info */}
        <ProfileInfo
          id={id}
          immigration_id={immigration_id}
          application_id={application_id}
          data={values}
          activeApplicationId={activeApplicationId}
          setActiveApplicationId={setActiveApplicationId}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />

        <Col col="12">
          <NavItems
            id={id}
            immigration_id={immigration_id}
            application_id={application_id}
            isDocumentsTab={isDocumentsTab}
          />

          <Card className="border-top-0 border-bottom-0 rounded-0 mb-0">
            <CardHeader>
              <CardTitle>{t("Application Documents")}</CardTitle>
            </CardHeader>

            <CardBody>
              <Row>
                <Col className="col-12">
                  <div className="mb-3">
                    <Dropzone
                      moduleData={{
                        module_app_id: 30,
                        module_main_id: id,
                        module_sub_id: application_id,
                        section_id: 0,
                        file_path: "immigration",
                      }}
                      attachmentForIDs={[]}
                      fetchData={true}
                      isOpen={false}
                      isDisabled={false}
                    />
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card className="border-top-0 rounded-0 rounded-bottom mb-0">
            <CardFooter className="d-flex flex-wrap gap-2 border-top justify-content-end sticy-footer bg-gray">
              <div className="d-flex gap-2"></div>

              <div className="ms-auto d-flex gap-2 justify-content-end">
                <Button
                  color="danger"
                  className="btn-block"
                  type="reset"
                  icon="cancel"
                  loading={loading}
                  onClick={() => handleRedirect(true)}
                >
                  {t("Cancel")}
                </Button>
              </div>
            </CardFooter>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default FormComponent;
